/**
 * Handing a generated file to the device.
 *
 * DYS-027 / DYS-028. The export path used to create a blob URL, create a
 * *detached* anchor, click it, and revoke the URL on the very next line. That
 * works on a laptop and is the classic iOS-only failure: Safari fetches the blob
 * asynchronously after the click, so a synchronous revoke can invalidate the URL
 * before the fetch starts, and older WebKit wants the anchor connected to the
 * document before it treats a synthetic click as a navigation.
 *
 * Two changes, and the order matters:
 *
 * 1. Offer the file to `navigator.share` when `navigator.canShare` accepts it.
 *    On iOS that is the Files / Drive / send-to sheet, which is the mechanism
 *    the client's reviewers described in R2 - and it is the only path that lets
 *    the user choose where the file lands. `<a download>` never can: the browser
 *    picks the folder and the page has no say.
 * 2. Otherwise use an anchor that is *in* the document and revoke on a later
 *    tick.
 *
 * The sheet is offered on touch-primary devices only. `canShare({ files })` is
 * also true in Safari and Chrome on a laptop, and there the sheet would be a
 * regression rather than a fix: a desktop share sheet offers AirDrop, Mail and
 * Messages and no way to put the file on disk, which is exactly what a teacher
 * preparing materials wants and what `<a download>` already does well. The
 * problem the sheet solves - no user-visible download folder, and in a
 * standalone install no address bar to show that anything happened - is a mobile
 * problem.
 *
 * `canShare` is called with the real file rather than probed for existence:
 * iOS 15 is in this app's audience and shipped `share` without `canShare`, and
 * even where both exist the sheet refuses some file types. Feature-detecting the
 * type is the only reliable question.
 *
 * DYS-043 adds the middle step the paragraph above deliberately left out. The
 * client asked to let the user choose where an export lands; on touch that was
 * already true, because the share sheet reaches Files and Drive, and Android
 * matches `(pointer: coarse)` just as iOS does. On a desktop it was not, and the
 * answer there is not the share sheet - for all the reasons above - but
 * `window.showSaveFilePicker()`, which opens the OS save dialog and writes the
 * bytes to wherever the user points it. Chrome and Edge have it; Firefox and
 * Safari do not and keep the anchor, which is what they have always done and
 * what has never been the broken case.
 *
 * So the order is: share sheet, then save dialog, then anchor - each step the
 * best answer its platform can give, each falling through to the next when the
 * platform cannot.
 *
 * The caller needs four outcomes, not two, because "the file exists now" is not
 * one fact:
 *
 * - A dismissed dialog is not a failure. `navigator.share` and
 *   `showSaveFilePicker` both reject with `AbortError` when the user backs out,
 *   and telling them the save failed would be a lie. It is also not a success -
 *   nothing was saved, so the unsaved-work warning has to stay armed.
 * - A file the *user* placed and a file the *browser* placed cannot share one
 *   sentence. "Plik znajdziesz w folderze Pobrane" is the honest thing to say
 *   about an anchor download and a false thing to say about a file the user just
 *   put on their desktop. Hence 'saved' beside 'downloaded'.
 */

/** What actually happened, from the user's point of view. */
export type SaveFileOutcome = 'shared' | 'saved' | 'downloaded' | 'cancelled'

/**
 * `showSaveFilePicker` is not in this project's DOM lib - TypeScript 5.9 ships
 * `FileSystemFileHandle` and `FileSystemWritableFileStream` but not the entry
 * point that hands one out, which lives in `@types/wicg-file-system-access`.
 * Declaring the one member locally is cheaper than a dependency, and optional so
 * that the feature detection below is a type-level truth as well as a runtime
 * one. Narrowed to the options this module actually passes.
 */
interface SaveFilePickerOptions {
  suggestedName?: string
  types?: { description?: string; accept: Record<string, string[]> }[]
}

declare global {
  interface Window {
    showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>
  }
}

/**
 * How long the blob URL stays alive after the click.
 *
 * The requirement is only "not the same tick" - WebKit starts reading the blob
 * within milliseconds. The number is generous rather than measured, because the
 * cost of being late is a download that silently does nothing and the cost of
 * being early is a few hundred kilobytes held for a moment.
 */
const BLOB_URL_LIFETIME_MS = 10_000

function errorName(error: unknown): string {
  // Matched on the name string, not `instanceof`: the DOMException subclasses
  // mangle to single letters in the production bundle while the names survive.
  return (error as { name?: string } | null)?.name ?? ''
}

/**
 * True when the OS share sheet will accept this file.
 *
 * Both `canShare` and `share` are optional - see the note above about iOS 15.
 * `canShare` itself throws on no arguments in some engines, never on a files
 * array, so the call is made directly.
 */
function canShareFile(file: File): boolean {
  if (typeof navigator === 'undefined') return false
  if (!isTouchPrimary()) return false
  if (typeof navigator.share !== 'function') return false
  if (typeof navigator.canShare !== 'function') return false
  return navigator.canShare({ files: [file] })
}

/**
 * True when the primary pointer is a finger - phones and tablets.
 *
 * Deliberately a capability query rather than a user-agent test. A Windows
 * laptop being used with a mouse reports a fine primary pointer even when the
 * screen is a touchscreen, which is the answer we want; an iPad reports coarse
 * whether or not a trackpad is attached, which is also the answer we want.
 * Anything that cannot be asked falls back to the download, because the download
 * is the path that already worked everywhere except iOS.
 */
function isTouchPrimary(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches
  } catch {
    return false
  }
}

/**
 * True when this browser can open a real OS save dialog.
 *
 * No secure-context test is needed: the API is exposed only on a secure origin,
 * so over plain HTTP the property is simply absent and this reads false.
 *
 * The touch-primary test is not redundant even though the share sheet is tried
 * first. That branch is only *taken* when `canShare` accepts the file, and it
 * falls through to here when it does not, or when the gesture had expired by the
 * time the sheet was asked for. A save dialog is the wrong thing to put in front
 * of someone holding a phone, so it is ruled out on the same capability query
 * that rules the sheet in.
 */
function hasSaveFilePicker(): boolean {
  if (typeof window === 'undefined') return false
  if (isTouchPrimary()) return false
  return typeof window.showSaveFilePicker === 'function'
}

/** The trailing `.docx` / `.jpg`, lower-cased, or '' when the name carries none. */
function fileExtension(filename: string): string {
  return /\.[A-Za-z0-9]+$/.exec(filename)?.[0].toLowerCase() ?? ''
}

/**
 * The file-type filter for the save dialog, derived from the two things the
 * caller already told us rather than hard-coded.
 *
 * Both callers matter and they do not agree: the text export sends a DOCX MIME
 * type and a `.docx` name, the picture export sends `image/jpeg` - or whatever
 * the fetched blob claims - and a `.jpg` name. Hard-coding either one would put
 * the wrong filter on the other's dialog.
 *
 * A name with no extension yields no `types` entry at all, rather than an entry
 * with an empty extension list. The dialog then shows no filter, which is a fine
 * thing for it to do; an entry that claims a type and lists nothing is not, and
 * the API validates these options strictly enough that it is not worth finding
 * out which engine throws on it.
 */
function pickerTypes(filename: string, mimeType: string): SaveFilePickerOptions['types'] {
  const extension = fileExtension(filename)
  if (!extension || !mimeType) return undefined

  return [
    {
      // User-facing, so Polish, and generic because the extension is all we know.
      description: `Plik ${extension.slice(1).toUpperCase()}`,
      accept: { [mimeType]: [extension] },
    },
  ]
}

/**
 * Offers the OS save dialog and writes the blob to whatever the user chose.
 *
 * Returns null to mean "this browser or this moment cannot do it, use the
 * anchor" - which is a normal result, not a failure, and is why the caller's
 * fallthrough is unconditional rather than wrapped in a feature check.
 */
async function saveViaFilePicker(
  blob: Blob,
  filename: string,
  mimeType: string,
): Promise<SaveFileOutcome | null> {
  let handle: FileSystemFileHandle | undefined
  try {
    handle = await window.showSaveFilePicker?.({
      suggestedName: filename,
      types: pickerTypes(filename, mimeType),
    })
  } catch (error) {
    // The user closed the dialog. Nothing was written and nothing should be:
    // downloading the file anyway would be the app overruling them.
    if (errorName(error) === 'AbortError') return 'cancelled'

    /*
     * The dialog needs *transient* user activation - roughly five seconds since
     * the click - and this is genuinely reachable rather than defensive. The
     * text export packs the whole DOCX with `await Packer.toBlob(doc)` before it
     * gets here, and on a long document that outlives the activation. Chrome
     * calls the result `SecurityError`, other engines `NotAllowedError`.
     *
     * Both fall through to the anchor, which needs no activation and is the path
     * that has always worked on a desktop. Losing the choice of folder is a much
     * smaller harm than a save button that does nothing on exactly the documents
     * that took the longest to prepare.
     */
    const name = errorName(error)
    if (name === 'SecurityError' || name === 'NotAllowedError') return null

    throw error
  }

  // Only reachable if the property vanished between the detection and the call,
  // which it cannot; the optional call keeps the detection honest at type level
  // rather than asserting it away.
  if (!handle) return null

  const writable = await handle.createWritable()
  try {
    await blobWrittenTo(writable, blob)
  } catch (error) {
    // A half-written file must not be reported as saved, and the stream holds a
    // lock on the file until it is closed one way or the other. Aborting it
    // releases that lock and discards the partial write; the original failure is
    // what the caller needs to hear about, so an abort that itself fails is
    // swallowed rather than allowed to mask it.
    await writable.abort().catch(() => {})
    throw error
  }

  return 'saved'
}

/**
 * The two calls that actually move the bytes, kept together so that 'saved' can
 * only be returned once `close()` has resolved. `write()` resolving means the
 * chunk was queued, not that it reached the disk - the flush happens in
 * `close()`, and so does a disk-full or permission failure.
 */
async function blobWrittenTo(writable: FileSystemWritableFileStream, blob: Blob): Promise<void> {
  await writable.write(blob)
  await writable.close()
}

/**
 * Downloads via an anchor that is connected to the document, with the revoke
 * deferred so it cannot race the browser's own read of the blob.
 */
function downloadViaAnchor(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.style.display = 'none'

  document.body.appendChild(anchor)
  anchor.click()

  setTimeout(() => {
    anchor.remove()
    URL.revokeObjectURL(url)
  }, BLOB_URL_LIFETIME_MS)
}

/**
 * Saves a generated file to the device: share sheet where the OS offers one,
 * save dialog where the browser offers one, download otherwise.
 *
 * Throws if the file cannot be handed over at all, so the caller can say so.
 * Returns 'cancelled' when the user dismissed the sheet or the dialog, which is
 * neither.
 */
export async function saveFileToDevice(
  blob: Blob,
  filename: string,
  mimeType: string,
): Promise<SaveFileOutcome> {
  const file = new File([blob], filename, { type: mimeType })

  if (canShareFile(file)) {
    try {
      await navigator.share({ files: [file] })
      return 'shared'
    } catch (error) {
      if (errorName(error) === 'AbortError') return 'cancelled'
      // NotAllowedError means the gesture has expired - the click happens after
      // the document has been packed, so this is reachable. The anchor does not
      // need user activation, so it is still worth trying.
      if (errorName(error) !== 'NotAllowedError') throw error
    }
  }

  if (hasSaveFilePicker()) {
    const outcome = await saveViaFilePicker(blob, filename, mimeType)
    if (outcome) return outcome
  }

  downloadViaAnchor(blob, filename)
  return 'downloaded'
}
